/**
 * WorkerEarnings.js - Worker earnings overview
 * Shows earnings summary and payment history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, TrendingUp, Calendar, Clock,
  ChevronRight, Download, Filter, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { earningsAPI } from '../../services/api';

const WorkerEarnings = () => {
  const [earnings, setEarnings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week'); // week, month, all
  
  const fetchEarnings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await earningsAPI.getWorkerEarnings();
      const data = response.data;
      
      setEarnings({
        total: data.total_earnings || 0,
        pending: data.pending_earnings || 0,
        paid: data.paid_earnings || 0,
        thisWeek: data.this_week || 0,
        thisMonth: data.this_month || 0,
        jobsCompleted: data.jobs_completed || 0,
        hoursWorked: data.hours_worked || 0,
        avgPerJob: data.avg_per_job || 0
      });
      
      setHistory(data.recent_earnings || []);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      // Use mock data if API fails
      setEarnings({
        total: 0,
        pending: 0,
        paid: 0,
        thisWeek: 0,
        thisMonth: 0,
        jobsCompleted: 0,
        hoursWorked: 0,
        avgPerJob: 0
      });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Earnings</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchEarnings}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-3 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="p-4 pb-24 space-y-4">
          {/* Main Earnings Card */}
          <div className="bg-gradient-to-br from-[var(--primary)] to-purple-600 rounded-2xl p-6 text-black">
            <p className="text-black/70 text-sm mb-1">Total Earnings</p>
            <p className="text-4xl font-bold mb-4">
              ${(typeof earnings?.total === 'number' ? earnings.total : 0).toFixed(2)}
            </p>
            
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-black/20">
              <div>
                <p className="text-black/70 text-xs">Pending</p>
                <p className="text-lg font-semibold">
                  ${(typeof earnings?.pending === 'number' ? earnings.pending : 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-black/70 text-xs">Paid Out</p>
                <p className="text-lg font-semibold">
                  ${(typeof earnings?.paid === 'number' ? earnings.paid : 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-sm text-[var(--text-muted)]">This Week</span>
              </div>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                ${(typeof earnings?.thisWeek === 'number' ? earnings.thisWeek : 0).toFixed(2)}
              </p>
            </div>
            
            <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-blue-500" />
                </div>
                <span className="text-sm text-[var(--text-muted)]">This Month</span>
              </div>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                ${(typeof earnings?.thisMonth === 'number' ? earnings.thisMonth : 0).toFixed(2)}
              </p>
            </div>
            
            <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-purple-500" />
                </div>
                <span className="text-sm text-[var(--text-muted)]">Jobs Done</span>
              </div>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                {earnings?.jobsCompleted || 0}
              </p>
            </div>
            
            <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-orange-500" />
                </div>
                <span className="text-sm text-[var(--text-muted)]">Avg/Job</span>
              </div>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                ${(typeof earnings?.avgPerJob === 'number' ? earnings.avgPerJob : 0).toFixed(2)}
              </p>
            </div>
          </div>
          
          {/* Recent Earnings */}
          <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl">
            <div className="px-4 py-3 border-b border-[var(--border-primary)] flex items-center justify-between">
              <h3 className="font-semibold text-[var(--text-primary)]">Recent Earnings</h3>
            </div>
            
            {history.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-[var(--background-subtle)] rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[var(--text-muted)]" />
                </div>
                <p className="text-[var(--text-muted)]">
                  Complete jobs to see your earnings history
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--border-primary)]">
                {history.map((item, index) => (
                  <div key={index} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[var(--text-primary)]">
                        {item.service_type?.charAt(0).toUpperCase() + item.service_type?.slice(1)} Job
                      </p>
                      <p className="text-sm text-[var(--text-muted)]">
                        {formatDate(item.completed_at || item.date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-500">
                        +${(item.worker_pay || item.amount || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {item.status || 'completed'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerEarnings;
